export type FeatureFlagName =
  | 'unlockAll'
  | 'unlockAllUsers'
  | 'disableMPI'
  | 'showSubmittedApps'
  | 'useMockoon';

type FeatureFlags = Record<FeatureFlagName, boolean>;

type FeatureFlagOverrides = Partial<FeatureFlags> & { enableMPI?: boolean };

const LOCALSTORAGE_OVERRIDES_KEY = 'DEV_FEATURE_FLAG_OVERRIDES';

// Tracked defaults (non-sensitive). Update these as needed.
export const featureFlags: FeatureFlags = {
  unlockAll: false,
  unlockAllUsers: false,
  disableMPI: false,
  showSubmittedApps: true,
  useMockoon: false,
};

function safeParseJson<T>(value: string | null): T | undefined {
  if (!value) return undefined;
  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
}

function getDisableMPIOverride(
  overrides: FeatureFlagOverrides | undefined,
): boolean | undefined {
  if (typeof overrides?.disableMPI === 'boolean') return overrides.disableMPI;
  if (typeof overrides?.enableMPI === 'boolean') return !overrides.enableMPI;
  return undefined;
}

export function getFeatureFlag(name: FeatureFlagName): boolean {
  const overrides = safeParseJson<FeatureFlagOverrides>(
    localStorage.getItem(LOCALSTORAGE_OVERRIDES_KEY),
  );

  if (name === 'disableMPI') {
    const mpiOverride = getDisableMPIOverride(overrides);
    return typeof mpiOverride === 'boolean' ? mpiOverride : featureFlags.disableMPI;
  }

  const override = overrides?.[name];
  return typeof override === 'boolean' ? override : featureFlags[name];
}

export function setFeatureFlagOverride(
  name: FeatureFlagName,
  value: boolean | null,
) {
  const current =
    safeParseJson<FeatureFlagOverrides>(
      localStorage.getItem(LOCALSTORAGE_OVERRIDES_KEY),
    ) ?? {};

  if (value === null) delete current[name];
  else current[name] = value;

  localStorage.setItem(LOCALSTORAGE_OVERRIDES_KEY, JSON.stringify(current));
}

export function clearAllFeatureFlagOverrides() {
  localStorage.removeItem(LOCALSTORAGE_OVERRIDES_KEY);
}
